import { uniqPrimativeMerge } from '../../utils/arrayUtils';
import { CoOpActionKeys as keys } from './coOpActionKeys';
import { CoOpActionTypes } from './coOpActionTypes';
import {
  ICoOpDealerEstimate,
  ICoOpGlobalSettings,
  ICoOpRegionBudget,
  ICoOpRequest,
  ICoOpRequestStatus,
  ICoOpSalesDistrictBudget
} from './coOpInterfaces';

export interface ICoOpReduxState extends ICoOpBaseState {
  dealerEstimates: { [index: string]: ICoOpDealerEstimate };
  globalSettings: { [index: string]: ICoOpGlobalSettings };
  regionBudgetHash: { [index: string]: ICoOpRegionBudget };
  requestStatuses: { [index: number]: ICoOpRequestStatus[] };
  salesDistrictBudgetHash: { [index: string]: ICoOpSalesDistrictBudget };
}

interface ICoOpBaseState {
  requestHash: { [index: number]: ICoOpRequest };
  requestIdsBySalesDistrict: { [index: string]: number[] };
  requestIdsByBusiness: { [index: number]: number[] };
}

const initState: ICoOpReduxState = {
  dealerEstimates: {},
  globalSettings: {},
  regionBudgetHash: {},
  requestHash: {},
  requestIdsByBusiness: {},
  requestIdsBySalesDistrict: {},
  requestStatuses: {},
  salesDistrictBudgetHash: {}
};

export function coOpReducer(state: ICoOpReduxState = initState, action: CoOpActionTypes): ICoOpReduxState {
  switch (action.type) {
    case keys.SET_COOP_ACTIVE_STATUS:
      return {
        ...state,
        requestHash: {
          ...state.requestHash,
          [action.payload.coOpRequestId]: {
            ...state.requestHash[action.payload.coOpRequestId],
            coOpStatusId: action.payload.coOpRequestStatusId,
            currentStatus: action.payload
          }
        },
        requestStatuses: {
          ...state.requestStatuses,
          [action.payload.coOpRequestId]: state.requestStatuses[action.payload.coOpRequestId]
            ? [
                ...state.requestStatuses[action.payload.coOpRequestId].filter(
                  x => x.coOpRequestStatusId !== action.payload.coOpRequestId
                ),
                action.payload
              ]
            : [action.payload]
        }
      };
    case keys.SET_COOP_DEALER_ESTIMATES:
      return {
        ...state,
        dealerEstimates: {
          ...state.dealerEstimates,
          ...action.payload.reduce<{ [index: string]: ICoOpDealerEstimate }>((hash, e) => {
            hash[`${e.fiscalYear}_${e.businessEntityId}`] = e;
            return hash;
          }, {})
        }
      };
    case keys.SET_COOP_GLOBAL_SETTINGS:
      return {
        ...state,
        globalSettings: {
          ...state.globalSettings,
          [action.payload.fiscalYear]: action.payload
        }
      };
    case keys.SET_COOP_REGION_BUDGETS:
      return {
        ...state,
        regionBudgetHash: action.payload.reduce<{ [index: string]: ICoOpRegionBudget }>(
          (hash, b) => {
            hash[`${b.fiscalYear}_${b.regionId}`] = b;
            return hash;
          },
          {
            ...state.regionBudgetHash
          }
        )
      };
    case keys.SET_COOP_REQUESTS:
      return {
        ...state,
        ...processCoOpRequests(action.payload, {
          requestHash: { ...state.requestHash },
          requestIdsByBusiness: { ...state.requestIdsByBusiness },
          requestIdsBySalesDistrict: { ...state.requestIdsBySalesDistrict }
        })
      };
    case keys.SET_COOP_REQUEST_STATUSES:
      return {
        ...state,
        requestStatuses: {
          ...state.requestStatuses,
          [action.payload.coOpRequestId]: action.payload.statuses
        }
      };
    case keys.SET_COOP_SALESDISTRICT_BUDGETS: {
      return {
        ...state,
        salesDistrictBudgetHash: action.payload.reduce<{ [index: string]: ICoOpSalesDistrictBudget }>(
          (hash, b) => {
            hash[`${b.fiscalYear}_${b.salesDistrictId}`] = b;
            return hash;
          },
          { ...state.salesDistrictBudgetHash }
        )
      };
    }
    default:
      return state;
  }
}

function processCoOpRequests(requests: ICoOpRequest[], existing: ICoOpBaseState): ICoOpBaseState {
  const base: ICoOpBaseState = { ...existing };

  requests.forEach(r => {
    base.requestHash[r.coOpRequestId] = r;

    base.requestIdsByBusiness[r.businessEntityId] = uniqPrimativeMerge(
      base.requestIdsByBusiness[r.businessEntityId],
      r.coOpRequestId
    );

    base.requestIdsBySalesDistrict[`${r.billToFiscal}_${r.salesDistrictId}`] = uniqPrimativeMerge(
      base.requestIdsBySalesDistrict[`${r.billToFiscal}_${r.salesDistrictId}`],
      r.coOpRequestId
    );
  });

  return base;
}
